import ArrowBack from '@mui/icons-material/ArrowBack';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import VpnKeyOutlined from '@mui/icons-material/VpnKeyOutlined';
import { AuthenticationClient } from '@src/authentication/AuthenticationClient';
import { Button } from '@src/common/components/button/Button';
import { Header } from '@src/common/components/Page';
import { Stack } from '@src/common/components/Stack';
import { TextInput } from '@src/common/components/TextInput';
import { Typography } from '@src/common/components/Typography';
import { css } from '@src/common/styles/Styles';
import { theme } from '@src/common/styles/Theme';
import { colors } from '@src/common/styles/valueTokens';
import { Navigation } from '@src/routing/Navigation';
import { useState } from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

export const ForgotPasswordPage = () => {
  type FormInputs = { email: string };
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const {
    register,
    watch,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<FormInputs>();
  const { email } = watch();

  const onSubmit: SubmitHandler<FormInputs> = async ({ email }) => {
    setLoading(true);
    try {
      await AuthenticationClient.forgotPassword(email);
      setSuccess(true);
    } catch (e) {
      if (e instanceof Error) {
        setError('email', { message: e.message || 'An error occurred' });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Stack className={page}>
      <Stack width={{ xs: '272px', md: '360px' }} gap={theme.spacing['4']}>
        {success ? (
          <Stack gap={theme.spacing['4']}>
            <MailOutlineIcon fontSize="large" />

            <Header title={t('forgotPassword.emailSentTitle')} />

            <Typography
              variant="body1"
              style={{ color: colors.text.secondary }}
            >
              {t('forgotPassword.emailSentMessage')}
              <span style={{ fontWeight: 700 }}>{email}</span>
            </Typography>
          </Stack>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)}>
            <Stack gap={theme.spacing['4']}>
              <VpnKeyOutlined fontSize="large" />

              <Header title={t('forgotPassword.title')} />

              <Typography style={{ color: colors.text.secondary }}>
                {t('forgotPassword.subtitle')}
              </Typography>
            </Stack>

            <Stack gap={theme.spacing['8']} marginTop={theme.spacing['6']}>
              <TextInput
                label={t('email')}
                {...register('email', { required: t('formErrors.isRequired') })}
                error={errors.email?.message}
                disabled={loading}
              />
              <Button
                type="submit"
                variant="contained"
                disabled={loading || !email}
              >
                {t('forgotPassword.sendResetCode')}
              </Button>
            </Stack>
          </form>
        )}

        <Stack direction="row" justifyContent="center">
          <Button
            onClick={() => navigate(Navigation.login)}
            startIcon={<ArrowBack fontSize="small" />}
          >
            {t('forgotPassword.backToLogin')}
          </Button>
        </Stack>
      </Stack>
    </Stack>
  );
};

const page = css(() => ({
  height: '100vh',
  backgroundColor: 'white',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
}));
